import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, TextField, Button, Typography, Paper, Box, Alert,
    InputAdornment, IconButton, GlobalStyles, Snackbar, CircularProgress, Divider
} from '@mui/material';
import { EmailRounded, LockRounded, Visibility, VisibilityOff, LoginRounded } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
// 🔥 1. નવું Google Login Import કર્યું
import { GoogleLogin } from '@react-oauth/google';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);

    // 🟢 જૂનું પાસવર્ડ વાળું લોગીન 
    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const res = await axios.post('/api/auth/login', { email, password });

            if (res.data && res.data.token) {
                setSuccessOpen(true);
                login(res.data.token, res.data.user);

                setTimeout(() => {
                    navigate('/');
                }, 1500);
            } else {
                setLoading(false);
                setError('Login failed: Invalid server response.');
            }
        } catch (err) {
            setLoading(false);
            setError(err.response?.data?.message || 'Invalid Email or Password');
        }
    };

    // 🔥 2. નવું Google Login હેન્ડલર
    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        setError('');

        try {
            // બેકએન્ડ પર Google નો ટોકન મોકલો
            const res = await axios.post('/api/auth/google', { token: credentialResponse.credential });

            if (res.data && res.data.token) {
                setSuccessOpen(true);
                login(res.data.token, res.data.user);

                setTimeout(() => {
                    navigate('/');
                }, 1500);
            }
        } catch (err) {
            setLoading(false);
            setError(err.response?.data?.message || 'Google Login Failed. Email not registered.');
        }
    };

    return (
        <>
            <GlobalStyles styles={{ body: { margin: 0, padding: 0 } }} />
            <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fbf2dd, #f9f9f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <Container maxWidth="xs">
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center', boxShadow: '0 12px 24px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: '#e8f0f0', color: '#1e4d4d', mb: 2 }}>
                            <LoginRounded sx={{ fontSize: 40 }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e4d4d', mb: 1, letterSpacing: '-0.5px' }}>
                            Welcome Back
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, px: 2 }}>
                            Enter your credentials to access the Mahargh WealthOS portal.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>{error}</Alert>
                        )}

                        <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField fullWidth placeholder="Email Address" variant="outlined" value={email} onChange={(e) => setEmail(e.target.value)} required
                                InputProps={{ startAdornment: (<InputAdornment position="start"><EmailRounded sx={{ color: '#9e9e9e' }} /></InputAdornment>), sx: { borderRadius: 3, bgcolor: '#fdfbf7' } }}
                            />
                            <TextField fullWidth placeholder="Password" type={showPassword ? 'text' : 'password'} variant="outlined" value={password} onChange={(e) => setPassword(e.target.value)} required
                                InputProps={{
                                    startAdornment: (<InputAdornment position="start"><LockRounded sx={{ color: '#9e9e9e' }} /></InputAdornment>),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#9e9e9e' }}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 3, bgcolor: '#fdfbf7' }
                                }}
                            />

                            <Button fullWidth type="submit" variant="contained" disabled={loading}
                                sx={{
                                    mx: 0, mt: 1, py: 1.5, borderRadius: 3, bgcolor: '#1e4d4d', fontWeight: 700, color: '#ffffff',
                                    boxShadow: '0 8px 16px rgba(30, 77, 77, 0.2)', '&:hover': { bgcolor: '#153636' }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'LOGIN TO PORTAL'}
                            </Button>

                            {/* 🔥 3. નવું OR Divider અને Google Button */}
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                                <Typography variant="body2" sx={{ px: 2, color: '#9e9e9e', fontWeight: 600 }}>OR</Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError('Google Login Failed. Please try again.')}
                                    useOneTap
                                    shape="rectangular"
                                    text="continue_with"
                                    size="large"
                                    width="100%"
                                />
                            </Box>

                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                જો પાસવર્ડ ભૂલાય ગયો હોય તો તમારા એડમિન પાસે પાસવર્ડ રીસેટ કરાવો.
                            </Typography>
                        </Box>
                    </Paper>
                </Container>
            </Box>

            <Snackbar open={successOpen} autoHideDuration={2000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" variant="filled" sx={{ width: '100%', borderRadius: 2, fontWeight: 700 }}>
                    લોગિન સફળ રહ્યું! રીડાયરેક્ટ થઈ રહ્યું છે...
                </Alert>
            </Snackbar>
        </>
    );
};

export default Login;